import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Sparkles, User, Menu, X, Search as SearchIcon } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import DealBanner from "@/components/DealBanner";

const NAV = [
  { to: "/catalog", label: "Catalog" },
  { to: "/library", label: "Research" },
  { to: "/documentation", label: "Documentation" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "Support" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, signIn } = useCustomerAuth();
  const { pathname } = useLocation();
  const [menu, setMenu] = useState(false);

  return (
    <header className="fixed top-0 inset-x-0 z-[500] bg-black/60 backdrop-blur-xl border-b border-white/10" data-testid="site-header">
      <DealBanner />
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 leading-none" data-testid="logo-link">
          <img src="/brand/owl-mark.jpg" alt="Molecularix Biolabs owl emblem" className="w-9 h-9 md:w-11 md:h-11 rounded-full object-cover ring-1 ring-[#D4AF37]/30" />
          <span className="flex flex-col">
            <span className="font-serif text-xl md:text-2xl tracking-tight">Molecularix</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#D4AF37]">Biolabs</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${pathname === n.to ? "text-[#D4AF37]" : "text-[#A1A1AA] hover:text-white"}`}>
              {n.label}
            </Link>
          ))}
          <Link to="/ambassador?view=login" data-testid="nav-elite-signin"
            className="group inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]/80 hover:text-[#D4AF37] transition-colors">
            <Sparkles size={12} className="transition-transform group-hover:scale-110" /> Elite
          </Link>
        </nav>

        <div className="flex items-center gap-4">
          <Link to="/catalog?focus=1" data-testid="header-search-btn" aria-label="Search the catalog"
            className="text-[#E8E8E3] hover:text-[#D4AF37] transition-colors">
            <SearchIcon size={19} strokeWidth={1.6} />
          </Link>
          {user ? (
            <Link to="/account" data-testid="header-account-avatar"
              className="hidden sm:flex items-center gap-2 group" title={user.email}>
              {user.picture ? (
                <img src={user.picture} alt={user.name || "Account"} className="w-8 h-8 rounded-full object-cover ring-1 ring-[#D4AF37]/40 group-hover:ring-[#D4AF37] transition-all" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-[#D4AF37]/15 flex items-center justify-center"><User size={15} color="#D4AF37" /></span>
              )}
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA] group-hover:text-white transition-colors max-w-[90px] truncate">{(user.name || user.email || "").split(" ")[0]}</span>
            </Link>
          ) : (
            <button onClick={signIn} data-testid="header-signin-btn"
              className="hidden sm:inline-flex items-center gap-1.5 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors">
              <User size={12} /> Sign in
            </button>
          )}
          <button onClick={() => setOpen(true)} data-testid="open-cart-btn"
            className="relative flex items-center gap-2 group" aria-label="Open cart">
            <ShoppingBag size={20} strokeWidth={1.4} className="group-hover:text-[#D4AF37] transition-colors" />
            {count > 0 && (
              <span className="absolute -top-2 -right-3 bg-[#D4AF37] text-black text-[10px] font-mono w-5 h-5 flex items-center justify-center rounded-full" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
          <button onClick={() => setMenu((v) => !v)} data-testid="mobile-menu-btn" aria-label="Menu" aria-expanded={menu}
            className="md:hidden text-[#E8E8E3] hover:text-[#D4AF37] transition-colors">
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menu && (
        <nav className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl px-6 py-5" data-testid="mobile-nav">
          <div className="flex flex-col gap-1">
            {[...NAV, { to: "/track", label: "Track Order" }, { to: "/account", label: "Account" }, { to: "/ambassador?view=login", label: "Elite Program" }].map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setMenu(false)} data-testid={`mobile-nav-${n.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`font-mono text-xs uppercase tracking-[0.2em] py-3 border-b border-white/5 transition-colors ${pathname === n.to ? "text-[#D4AF37]" : "text-[#A1A1AA] hover:text-white"}`}>
                {n.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
